import type { GeneralParametersOverviewResponse, ParameterEditabilityDto, ParameterOverviewRow } from './generalParametersApiTypes';

interface SummaryCard {
  key: string;
  label: string;
  count: number;
}

interface FilterItem {
  code: string;
  label: string;
  count: number;
}

export interface GeneralParameterRowModel {
  code: string;
  title: string;
  subtitle: string;
  category: string;
  kind: string;
  kindLabel: string;
  source: string;
  entries: number;
  regionLabel: string;
  lastModified: string;
  lastModifiedBy: string;
  canOpenDetail: boolean;
  editability: ParameterEditabilityDto;
}

export interface GeneralParametersViewModel {
  totalCount: number;
  filteredCount: number;
  summaryCards: SummaryCard[];
  categoryFilters: FilterItem[];
  regionFilters: FilterItem[];
  rows: GeneralParameterRowModel[];
}

const countFor = (overview: GeneralParametersOverviewResponse, kind: string) =>
  overview.filterCounts.kinds?.find((item) => item.code === kind)?.count ?? 0;

const displayKind = (kind: string) => kind || 'Unknown';

export const formatGeneralParameterDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getUTCMonth()];
  return `${day} ${month} ${date.getUTCFullYear()}`;
};

const mapRow = (row: ParameterOverviewRow): GeneralParameterRowModel => ({
  code: row.code,
  title: row.name,
  subtitle: row.description ?? row.target ?? row.sourceStandard ?? '',
  category: row.category,
  kind: row.kind,
  kindLabel: displayKind(row.kind),
  source: row.source,
  entries: row.entries,
  regionLabel: row.regions.map((region) => region.code).join(', ') || '—',
  lastModified: formatGeneralParameterDate(row.lastModifiedAt),
  lastModifiedBy: row.lastModifiedBy ?? '—',
  canOpenDetail: ['List', 'Map', 'Mirror'].includes(row.kind),
  editability: row.editability,
});

export const buildGeneralParametersViewModel = (overview: GeneralParametersOverviewResponse): GeneralParametersViewModel => {
  const listCount = countFor(overview, 'List');
  const mapCount = countFor(overview, 'Map');
  const mirrorCount = countFor(overview, 'Mirror');

  return {
    totalCount: listCount + mapCount + mirrorCount,
    filteredCount: overview.filteredCount,
    summaryCards: [
      { key: 'all', label: 'All parameters', count: listCount + mapCount + mirrorCount },
      { key: 'List', label: 'Lists', count: listCount },
      { key: 'Map', label: 'Maps', count: mapCount },
      { key: 'Mirror', label: 'Mirrors', count: mirrorCount },
    ],
    categoryFilters: [
      { code: '', label: 'All', count: listCount + mapCount + mirrorCount },
      ...(overview.filterCounts.categories ?? []),
    ],
    regionFilters: [
      { code: '', label: 'All regions', count: listCount + mapCount + mirrorCount },
      ...(overview.filterCounts.regions ?? []),
    ],
    rows: overview.rows.map(mapRow),
  };
};
